import { useState, useCallback } from 'react'
import { assertModelsConfigContract, getModelsConfig, type AppConfig } from '@/api/config'

let globalConfig: AppConfig | null = null
let globalConfigCallbacks: Array<(config: AppConfig | null) => void> = []

const notifyConfigAll = (config: AppConfig | null) => {
  globalConfigCallbacks.forEach((cb) => cb(config))
}

export function setAppConfig(config: AppConfig): void {
  globalConfig = config
  notifyConfigAll(config)
}

export async function refreshModelsConfig(): Promise<boolean> {
  try {
    const fresh = await getModelsConfig()
    if (!globalConfig || !fresh) return false
    assertModelsConfigContract(fresh)
    setAppConfig({
      ...globalConfig,
      models: fresh.models ?? globalConfig.models,
      generation_entries: fresh.entries ?? globalConfig.generation_entries,
      models_contract_version: fresh.contract_version,
      pricing_config_version: fresh.pricing_config_version,
      pricing_config_sha256: fresh.pricing_config_sha256,
      generation_entry_config_bundle_id: fresh.generation_entry_config_bundle_id,
    })
    return true
  } catch {
    // Keep the last known projection, but let CONFIG_STALE callers report that
    // the required refresh did not complete instead of claiming it succeeded.
    return false
  }
}

export function getGlobalConfig(): AppConfig | null {
  return globalConfig
}

export function useAppConfig(): AppConfig | null {
  const [config, setConfig] = useState<AppConfig | null>(globalConfig)

  const register = useCallback((cb: (c: AppConfig | null) => void) => {
    globalConfigCallbacks.push(cb)
    return () => {
      globalConfigCallbacks = globalConfigCallbacks.filter((x) => x !== cb)
    }
  }, [])

  useState(() => {
    const unsubscribe = register(setConfig)
    return unsubscribe
  })

  return config
}
